import { takeLatest, call, all, put, select } from 'redux-saga/effects';

// Services
import { linesApi } from 'services/apiDigitalTicket/lines';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Helpers
import { geoJsonFactory } from 'helpers/lines';
import { getJsonData } from 'helpers/getJsonData';
import { isString } from 'lodash';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';

import * as LinesActions from './actions';
import { LinesTypes, LineInterface } from './types';

function* list({
  payload,
}: ReturnType<typeof LinesActions.listLinesRequest>): any {
  try {
    const { body, recovers, loading = true, filtering } = payload;

    if (loading && !filtering) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando linhas' }));
    }

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(linesApi.list, {
      params: {
        transportOperatorId: transportOperator.id,
        ...payload.body,
        ...payload.params,
      },
    });

    if (response.status === 200) {
      let lines = response.data;
      const recoverPathRoute = recovers && recovers.includes('pathroute');

      if (recoverPathRoute) {
        const pathRoutesRes = yield all(
          lines.map((line: LineInterface) =>
            line.Sections?.map(({ LineSection }) => {
              // if the path already has the geojson url
              if (isString(LineSection.route)) {
                return call(() => getJsonData(LineSection.route as string));
              }
              // if the path does not have geojson
              const lineStops =
                line.LineStops?.filter(
                  ({ lineSectionId }) => lineSectionId === LineSection.id,
                ).sort((a, b) => a.order - b.order) || [];
              return call(() => geoJsonFactory(lineStops));
            }),
          ),
        );

        if (Array.isArray(pathRoutesRes)) {
          lines = lines.map((line: LineInterface, i: number) => ({
            ...line,
            Sections: line.Sections?.map((s, j) => ({
              ...s,
              LineSection: { ...s.LineSection, route: pathRoutesRes[i][j] },
            })),
          }));
        }
      }

      yield put(
        LinesActions.listLinesSuccess({
          linesList: lines,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      if (loading && !filtering) yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('lines - listLines - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(LinesActions.listLinesFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
  meta,
}: ReturnType<typeof LinesActions.filterLinesRequest>): any {
  try {
    const transportOperator = yield select((state) => {
      if ((payload as { useSelectedOperator: boolean })?.useSelectedOperator) {
        return state.operators.operatorSelected;
      }
      return state.transportOperators.transportOperatorSelected;
    });
    const response = yield call(linesApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        LinesActions.filterLinesSuccess({
          linesList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      meta.promise.resolve(response.data);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('stops - filterLines - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(LinesActions.filterLinesFailure());
    meta.promise.reject(error);
  }
}

function* get({
  payload,
  meta,
}: ReturnType<typeof LinesActions.getLineRequest>): any {
  try {
    const { params, recovers, loading = true } = payload;

    if (loading) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando linha' }));
    }

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(linesApi.get, {
      params: {
        transportOperatorId: transportOperator.id,
        ...params,
        ...payload.body,
      },
    });

    if (response.status === 200) {
      const line = response.data;
      const recoverPathRoute = recovers && recovers.includes('pathroute');

      if (recoverPathRoute && line?.LineSections) {
        const pathRoutesRes = yield all(
          line.LineSections?.map((LineSection: any) => {
            // if the path already has the geojson url
            if (isString(LineSection.route)) {
              return call(() => getJsonData(LineSection.route as string));
            }
            // if the path does not have geojson
            const lineStops =
              line.LineStops?.filter(
                ({ lineSectionId }: any) => lineSectionId === LineSection.id,
              ).sort((a: any, b: any) => a.order - b.order) || [];
            return call(() => geoJsonFactory(lineStops));
          }),
        );

        if (Array.isArray(pathRoutesRes)) {
          line.LineSections = line.LineSections?.map((ls: any, i: number) => ({
            ...ls,
            route: pathRoutesRes[i],
          }));
        }
      }

      yield put(LinesActions.getLineSuccess({ lineData: line }));
      yield meta.promise.resolve(response);
      if (loading) yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('lines - getLine - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(LinesActions.getLineFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}

function* create({
  payload,
  meta,
}: ReturnType<typeof LinesActions.createLineRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando linha' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(linesApi.create, {
      data: {
        transportOperatorId: transportOperator.id,
        ...payload,
      },
    });

    if (response.status === 200) {
      yield put(LinesActions.createLineSuccess({ lineData: response.data }));

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Linha criada com sucesso',
        }),
      );

      yield meta.promise.resolve(response);
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('lines - createLine - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(LinesActions.createLineFailure());
    yield meta.promise.reject(error);
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
  meta,
}: ReturnType<typeof LinesActions.updateLineRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando linha' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(linesApi.update, {
      data: {
        transportOperatorId: transportOperator.id,
        ...payload,
      },
    });

    if (response.status === 200) {
      yield put(LinesActions.updateLineSuccess());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Linha editada com sucesso',
        }),
      );

      yield meta.promise.resolve(response);
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('lines - updateLine - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(LinesActions.updateLineFailure());
    yield meta.promise.reject(error);
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof LinesActions.deleteLineRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo linha' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(linesApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(LinesActions.deleteLineSuccess());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Linha excluída com sucesso',
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        LinesActions.listLinesRequest({
          params: { _includes: 'lot,stops,benefits' },
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('lines - removeLine - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(LinesActions.deleteLineFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateBenefits({
  payload,
  meta,
}: ReturnType<typeof LinesActions.updateLineBenefitsRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Atrelando política(s)',
      }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(linesApi.updateBenefits, {
      data: {
        transportOperatorId: transportOperator.id,
        ...payload,
      },
    });

    if (response.status === 200) {
      yield put(LinesActions.updateLineBenefitsSuccess());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Política(s) tarifária(s) atrelada(s) com sucesso',
        }),
      );

      yield meta.promise.resolve(response);
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('lines - updateLineBenefits - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(LinesActions.updateLineBenefitsFailure());
    yield meta.promise.reject(error);
    yield put(DialogsActions.setClose());
  }
}

function* conectivity({
  payload,
}: ReturnType<typeof LinesActions.conectivityRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Carregando dados',
      }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(linesApi.conectivity, {
      data: {
        transportOperatorId: transportOperator.id,
        ...payload,
      },
    });

    if (response.status === 200) {
      if (payload.type === 'table') {
        yield put(
          LinesActions.conectivityDataPlanSuccess({
            conectivityDataPlanList: response.data,
          }),
        );
      } else {
        yield put(
          LinesActions.conectivitySuccess({ conectivityData: response.data }),
        );
      }

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('conectivity - error', error);
    if (payload.isDataPlan) {
      yield put(
        LinesActions.conectivityFailure({ conectivityError: internalCode }),
      );
    }
    if (!payload.isDataPlan)
      yield put(LinesActions.conectivityFailure({ internalCode }));

    yield put(DialogsActions.setClose());
  }
}

function* updateLineFare({
  payload,
  meta,
}: ReturnType<typeof LinesActions.updateLineFareRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Editando tarifa da linha' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(linesApi.updateLineFare, {
      data: {
        transportOperatorId: transportOperator.id,
        ...payload,
      },
    });

    if (response.status === 200) {
      yield put(LinesActions.updateLineFareSuccess());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Tarifa da Linha editada com sucesso',
        }),
      );

      yield meta.promise.resolve(response);
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('lines - updateLine - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(LinesActions.updateLineFareFailure());
    yield meta.promise.reject(error);
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const linesSagas = [
  takeLatest(LinesTypes.LIST_LINES_REQUEST, list),
  takeLatest(LinesTypes.FILTER_LINES_REQUEST, filter),
  takeLatest(LinesTypes.GET_LINE_REQUEST, get),
  takeLatest(LinesTypes.CREATE_LINE_REQUEST, create),
  takeLatest(LinesTypes.UPDATE_LINE_REQUEST, update),
  takeLatest(LinesTypes.DELETE_LINE_REQUEST, remove),
  takeLatest(LinesTypes.UPDATE_LINE_BENEFITS_REQUEST, updateBenefits),
  takeLatest(LinesTypes.CONECTIVITY_REQUEST, conectivity),
  takeLatest(LinesTypes.UPDATE_LINE_FARE_REQUEST, updateLineFare),
];
